import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSearch, faTimes,
  faCalendarAlt, faClock, faMapMarkerAlt, faImage,
  faSpinner, faAlignLeft, faChevronRight, faInfoCircle,
  faTrashAlt, faCheckCircle, faExclamationCircle, faEye
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import { Link } from 'react-router-dom';

const API_URL = `${API_CONFIG.apiUrl}evenements.php`;

// ─── Sous-composants ─────────────────────────────────────────────

const EventCard = ({ event, onEdit, onDelete }) => {
  const { id, titres, description, adresse, dates, heures, images } = event;
  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="event-card">
      {images && (
        <div className="card-image-wrapper">
          <img src={`${API_CONFIG.apiUrl}${images}`} alt={titres} className="card-image" loading="lazy" />
        </div>
      )}
      <div className="card-content">
        <div className="card-title">{titres}</div>
        {description && <div className="card-description">{description}</div>}
        <div className="card-infos">
          <div className="info-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>{formatDate(dates)} à {heures}</span>
          </div>
          <div className="info-item">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            <span>{adresse}</span>
          </div>
        </div>
        <div className="card-actions">
          <Link className="btn-sm btn-view" to={`/showEvent/${id}`}  aria-label="Voir">
            <FontAwesomeIcon icon={faEye} /> Voire
          </Link>
           <button className="btn-sm btn-edit" onClick={() => onEdit(event)} aria-label="Modifier">
            <FontAwesomeIcon icon={faEdit} /> Modifier
          </button>
          <button className="btn-sm btn-delete" onClick={() => onDelete(id)} aria-label="Supprimer">
            <FontAwesomeIcon icon={faTrash} /> Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

const SearchBar = ({ value, onChange, onClear }) => (
  <div className="search-bar">
    <FontAwesomeIcon icon={faSearch} className="search-icon" />
    <input
      type="text"
      placeholder="Rechercher un événement..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Rechercher"
    />
    {value && (
      <button type="button" className="clear-search" onClick={onClear} aria-label="Effacer la recherche">
        <FontAwesomeIcon icon={faTimes} />
      </button>
    )}
  </div>
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <FontAwesomeIcon icon={type === 'success' ? faCheckCircle : faExclamationCircle} />
      <span>{message}</span>
      <button onClick={onClose} aria-label="Fermer notification">
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  );
};

const DeleteConfirmModal = ({ isOpen, onConfirm, onCancel, eventTitle }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay active" onClick={onCancel}>
      <div className="modal-content delete-confirm" onClick={(e) => e.stopPropagation()}>
        <h3>
          <FontAwesomeIcon icon={faTrashAlt} style={{ color: '#ef4444' }} /> Confirmer la suppression
        </h3>
        <p>Voulez-vous vraiment supprimer l’événement <strong>"{eventTitle}"</strong> ? Cette action est irréversible.</p>
        <div className="form-actions">
          <button className="btn-cancel" onClick={onCancel}>Annuler</button>
          <button className="btn-danger" onClick={onConfirm}>
            <FontAwesomeIcon icon={faTrash} /> Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────

const EvenementList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Modal de création/édition
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    titres: '',
    description: '',
    adresse: '',
    dates: '',
    heures: '',
    images: null
  });
  const [formImagePreview, setFormImagePreview] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState(null); // pour suppression
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const fileInputRef = useRef(null);

  // Toast
  const [toast, setToast] = useState({ message: '', type: 'success', visible: false });

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, eventId: null, eventTitle: '' });

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Chargement
  const fetchEvents = useCallback(async (search = '') => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ action: 'list' });
      if (search) params.append('search', search);
      const res = await fetch(`${API_URL}?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.status === 'success') {
        setEvents(data.data || []);
      } else {
        throw new Error(data.message || 'Erreur de chargement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents(debouncedSearch);
  }, [debouncedSearch, fetchEvents]);

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingId(null);
    setFormData({ titres: '', description: '', adresse: '', dates: '', heures: '', images: null });
    setFormImagePreview(null);
    setExistingImagePath(null);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (event) => {
    setEditingId(event.id);
    setFormData({
      titres: event.titres,
      description: event.description || '',
      adresse: event.adresse,
      dates: event.dates,
      heures: event.heures,
      images: null
    });
    setFormImagePreview(event.images ? `${API_CONFIG.apiUrl}${event.images}` : null);
    setExistingImagePath(event.images || null);
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ titres: '', description: '', adresse: '', dates: '', heures: '', images: null });
    setFormImagePreview(null);
    setExistingImagePath(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Gestion des champs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, images: file }));
      const reader = new FileReader();
      reader.onloadend = () => setFormImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, images: null }));
      setFormImagePreview(null);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, images: null }));
    setFormImagePreview(null);
    setExistingImagePath(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Validation formulaire
  const validateForm = () => {
    const { titres, description, adresse, dates, heures } = formData;
    if (!titres.trim()) return 'Le titre est requis.';
    if (!description.trim()) return 'La description est requise.';
    if (!adresse.trim()) return 'L’adresse est requise.';
    if (!dates) return 'La date est requise.';
    if (!heures) return 'L’heure est requise.';
    // Vérifier que la date n'est pas dans le passé (optionnel)
    const selectedDate = new Date(`${dates}T${heures}`);
    if (selectedDate < new Date()) {
      return 'La date et l’heure doivent être dans le futur.';
    }
    return null;
  };

  // Soumission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('titres', formData.titres);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('adresse', formData.adresse);
      formDataToSend.append('dates', formData.dates);
      formDataToSend.append('heures', formData.heures);
      
      if (formData.images && formData.images instanceof File) {
        formDataToSend.append('images', formData.images);
      } else if (editingId && existingImagePath) {
        // Conserver l'image existante si elle n'a pas été modifiée
        formDataToSend.append('existing_image', existingImagePath);
      }
      // Si on veut supprimer l'image, on peut envoyer un flag, mais ici on ne le fait pas explicitement.
      // On pourrait ajouter un champ 'remove_image' si l'utilisateur clique sur "retirer l'image".

      let url = `${API_URL}?action=create`;
      if (editingId) {
        url = `${API_URL}?action=update&id=${editingId}`;
        formDataToSend.append('_method', 'PUT');
      }

      const res = await fetch(url, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await res.json();
      if (data.status === 'success') {
        closeModal();
        showToast(editingId ? 'Événement mis à jour avec succès' : 'Événement créé avec succès', 'success');
        fetchEvents(debouncedSearch);
      } else {
        setFormError(data.message || 'Erreur lors de l’enregistrement');
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Suppression (déclenche la confirmation)
  const handleDelete = (id) => {
    const event = events.find(ev => ev.id === id);
    if (event) {
      setDeleteConfirm({ isOpen: true, eventId: id, eventTitle: event.titres });
    }
  };

  const confirmDelete = async () => {
    const { eventId } = deleteConfirm;
    try {
      const res = await fetch(`${API_URL}?action=delete&id=${eventId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        showToast('Événement supprimé avec succès', 'success');
        fetchEvents(debouncedSearch);
      } else {
        setError(data.message || 'Erreur de suppression');
        showToast(data.message || 'Erreur de suppression', 'error');
      }
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setDeleteConfirm({ isOpen: false, eventId: null, eventTitle: '' });
    }
  };

  // Raccourci clavier (Escape pour fermer modal)
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showModal) closeModal();
        if (deleteConfirm.isOpen) setDeleteConfirm({ isOpen: false, eventId: null, eventTitle: '' });
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showModal, deleteConfirm.isOpen]);

  // Squelette de chargement
  const renderSkeletons = () => (
    <div className="event-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="event-card skeleton">
          <div className="skeleton-image"></div>
          <div className="skeleton-title"></div>
          <div className="skeleton-text"></div>
          <div className="skeleton-text short"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="event-wrapper mt-5">
      {/* Styles (améliorés) */}
      <style>{`
        /* ================================================================
           PREMIUM DARK GLASSMORPHISM V2
           ================================================================ */
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

        :root {
          --bg-dark: #05050a;
          --card-bg: rgba(255, 255, 255, 0.03);
          --card-border: rgba(255, 255, 255, 0.08);
          --text-main: #f8fafc;
          --text-muted: #94a3b8;
          --accent-primary: #6366f1;
          --accent-gradient: linear-gradient(135deg, #6366f1, #a855f7);
          --accent-glow: rgba(99, 102, 241, 0.3);
          --danger: #ef4444;
          --danger-bg: rgba(239, 68, 68, 0.1);
          --warning: #f59e0b;
          --warning-bg: rgba(245, 158, 11, 0.1);
          --success: #22c55e;
          --success-bg: rgba(34, 197, 94, 0.1);
        }

        .event-wrapper {
          max-width: 1600px;
          margin: 50px auto 0;
          padding: 2.5rem;
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--text-main);
          background: radial-gradient(circle at 15% 50%, rgba(99, 102, 241, 0.10), transparent 50%),
                      radial-gradient(circle at 85% 30%, rgba(168, 85, 247, 0.10), transparent 50%),
                      var(--bg-dark);
          border-radius: 32px;
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.05);
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .event-wrapper::before {
          content: '';
          position: absolute;
          top: 0; left: 10%; right: 10%; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.5), transparent);
          opacity: 0.6;
        }

        /* HEADER */
        .event-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1.5rem;
          margin-bottom: 2.5rem;
          position: relative;
          z-index: 10;
        }

        .event-header h2 {
          font-weight: 800;
          font-size: 2.2rem;
          margin: 0;
          letter-spacing: -0.03em;
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .event-header h2 .icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 14px;
          color: #a855f7;
          box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        }
        .event-count {
          font-size: 1rem;
          font-weight: 500;
          color: var(--text-muted);
          background: var(--card-bg);
          padding: 0.3rem 1rem;
          border-radius: 100px;
          border: 1px solid var(--card-border);
        }

        .actions-top {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          align-items: center;
        }

        /* SEARCH BAR */
        .search-bar {
          display: flex;
          align-items: center;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(10px);
          border: 1px solid var(--card-border);
          border-radius: 100px;
          padding: 0.4rem 1.2rem 0.4rem 1.5rem;
          transition: all 0.3s ease;
          width: 280px;
          position: relative;
        }
        .search-bar:focus-within {
          border-color: rgba(168, 85, 247, 0.5);
          box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.15);
          background: rgba(0,0,0,0.6);
        }
        .search-bar input {
          background: transparent;
          border: none;
          color: var(--text-main);
          outline: none;
          width: 100%;
          font-size: 0.95rem;
          padding: 0.6rem 0.8rem;
          font-family: inherit;
        }
        .search-bar input::placeholder {
          color: var(--text-muted);
        }
        .search-bar .search-icon {
          color: var(--text-muted);
          transition: color 0.3s;
        }
        .search-bar:focus-within .search-icon {
          color: #a855f7;
        }
        .clear-search {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 0 0.4rem;
          font-size: 1rem;
          transition: color 0.2s;
        }
        .clear-search:hover {
          color: #fff;
        }

        /* BUTTONS */
        .btn-primary {
          background: var(--accent-gradient);
          border: none;
          border-radius: 100px;
          padding: 0.8rem 2rem;
          color: #fff;
          font-weight: 600;
          font-size: 0.95rem;
          font-family: inherit;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          box-shadow: 0 10px 25px var(--accent-glow);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .btn-primary::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .btn-primary:hover::after {
          opacity: 1;
        }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(168, 85, 247, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-sm {
          padding: 8px 16px;
          font-size: 0.85rem;
          border-radius: 12px;
          border: 1px solid transparent;
          cursor: pointer;
          font-weight: 600;
          font-family: inherit;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
         .btn-view {
          background: var(--warning-bg);
          color: var(--success);
          border-color: rgba(11, 245, 70, 0.3);
        }  
        .btn-view:hover {
          background: rgba(7, 142, 29, 0.4);
          transform: scale(1.05);
        }

        .btn-edit {
          background: var(--warning-bg);
          color: var(--warning);
          border-color: rgba(245, 158, 11, 0.2);
        }

        .btn-edit:hover {
          background: rgba(245, 158, 11, 0.2);
          transform: scale(1.05);
        }
        .btn-delete {
          background: var(--danger-bg);
          color: var(--danger);
          border-color: rgba(239, 68, 68, 0.2);
        }
        .btn-delete:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: scale(1.05);
        }
        .btn-danger {
          background: var(--danger);
          border: none;
          border-radius: 100px;
          padding: 0.8rem 2rem;
          color: #fff;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
        }
        .btn-danger:hover {
          background: #dc2626;
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(239,68,68,0.3);
        }

        /* GRID & CARDS */
        .event-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 2rem;
          position: relative;
          z-index: 10;
        }

        .event-card {
          background: var(--card-bg);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
          animation: fadeUp 0.6s ease forwards;
          opacity: 0;
          transform: translateY(20px);
          position: relative;
        }
        .event-card:nth-child(1) { animation-delay: 0.05s; }
        .event-card:nth-child(2) { animation-delay: 0.10s; }
        .event-card:nth-child(3) { animation-delay: 0.15s; }
        .event-card:nth-child(4) { animation-delay: 0.20s; }
        .event-card:nth-child(5) { animation-delay: 0.25s; }
        .event-card:nth-child(6) { animation-delay: 0.30s; }

        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .event-card:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(168, 85, 247, 0.3);
          transform: translateY(-5px) scale(1.01);
          box-shadow: 0 20px 50px rgba(0,0,0,0.5), 0 0 30px rgba(168, 85, 247, 0.08);
        }

        .card-image-wrapper {
          width: 100%;
          height: 190px;
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 1.2rem;
          position: relative;
          background: rgba(0,0,0,0.3);
        }
        .card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .event-card:hover .card-image {
          transform: scale(1.05);
        }

        .card-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .card-title {
          font-weight: 700;
          font-size: 1.25rem;
          color: var(--text-main);
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .card-description {
          color: var(--text-muted);
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .card-infos {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          margin-top: auto;
          padding-bottom: 1.2rem;
          border-bottom: 1px solid var(--card-border);
        }
        .info-item {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          color: #cbd5e1;
          font-size: 0.9rem;
        }
        .info-item svg {
          color: #a855f7;
          width: 16px;
        }
        .card-actions {
          display: flex;
          gap: 0.8rem;
          margin-top: 1.2rem;
          justify-content: flex-end;
        }

        /* SKELETON */
        .skeleton .skeleton-image {
          width: 100%;
          height: 190px;
          border-radius: 16px;
          background: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          margin-bottom: 1.2rem;
        }
        .skeleton .skeleton-title {
          width: 70%;
          height: 1.2rem;
          background: rgba(255,255,255,0.05);
          border-radius: 8px;
          margin-bottom: 0.8rem;
          animation: shimmer 1.5s infinite;
          background-size: 200% 100%;
        }
        .skeleton .skeleton-text {
          width: 100%;
          height: 0.8rem;
          background: rgba(255,255,255,0.04);
          border-radius: 6px;
          margin-bottom: 0.6rem;
          animation: shimmer 1.5s infinite;
          background-size: 200% 100%;
        }
        .skeleton .skeleton-text.short {
          width: 60%;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        /* MODAL */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }
        .modal-overlay.active {
          opacity: 1;
          visibility: visible;
        }
        .modal-content {
          background: #0a0a12;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 28px;
          max-width: 650px;
          width: 100%;
          padding: 2.5rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);
          transform: scale(0.95) translateY(20px);
          transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
          position: relative;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-overlay.active .modal-content {
          transform: scale(1) translateY(0);
        }
        .modal-content.delete-confirm {
          max-width: 480px;
          text-align: center;
        }
        .modal-close {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-size: 1.5rem;
          cursor: pointer;
          transition: color 0.2s;
        }
        .modal-close:hover { color: #fff; }

        .modal-content h3 {
          font-weight: 700;
          font-size: 1.8rem;
          margin-top: 0;
          margin-bottom: 2rem;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }
        .modal-content h3 svg { color: #a855f7; }

        .form-group { margin-bottom: 1.5rem; }
        .modal-content label {
          display: block;
          font-weight: 600;
          color: var(--text-muted);
          margin-bottom: 0.5rem;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .modal-content input[type="text"],
        .modal-content input[type="date"],
        .modal-content input[type="time"],
        .modal-content textarea {
          width: 100%;
          padding: 1rem 1.2rem;
          border-radius: 14px;
          border: 1px solid var(--card-border);
          background: rgba(255,255,255,0.02);
          color: #fff;
          font-size: 1rem;
          font-family: inherit;
          transition: all 0.3s ease;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
        }
        .modal-content input:focus,
        .modal-content textarea:focus {
          border-color: #a855f7;
          background: rgba(255,255,255,0.05);
          outline: none;
          box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.15);
        }
        .modal-content textarea {
          min-height: 120px;
          resize: vertical;
        }
        .file-input-wrapper {
          position: relative;
          width: 100%;
        }
        .modal-content input[type="file"] {
          width: 100%;
          padding: 0.8rem;
          background: rgba(255,255,255,0.02);
          border: 1px dashed var(--text-muted);
          border-radius: 14px;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.3s;
        }
        .modal-content input[type="file"]:hover {
          border-color: #a855f7;
          color: #fff;
          background: rgba(168, 85, 247, 0.05);
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }
        .preview-image {
          width: 100%;
          height: 180px;
          object-fit: cover;
          border-radius: 14px;
          margin-top: 1rem;
          border: 1px solid var(--card-border);
          position: relative;
        }
        .preview-wrapper {
          position: relative;
        }
        .remove-image-btn {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          background: rgba(0,0,0,0.7);
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .remove-image-btn:hover {
          background: var(--danger);
          transform: scale(1.1);
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--card-border);
        }
        .btn-cancel {
          background: transparent;
          color: var(--text-main);
          border: 1px solid var(--card-border);
          border-radius: 100px;
          padding: 0.8rem 2rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-cancel:hover {
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.2);
        }
        .error-message {
          color: var(--danger);
          background: var(--danger-bg);
          border: 1px solid rgba(239,68,68,0.2);
          padding: 1rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* TOAST */
        .toast-container {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          max-width: 400px;
        }
        .toast {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          padding: 1rem 1.5rem;
          background: rgba(10,10,18,0.95);
          backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
          color: #fff;
          animation: slideIn 0.3s ease;
        }
        .toast-success { border-left: 4px solid var(--success); }
        .toast-error { border-left: 4px solid var(--danger); }
        .toast button {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          margin-left: auto;
          font-size: 1.2rem;
          transition: color 0.2s;
        }
        .toast button:hover { color: #fff; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }

        /* ETATS (Empty) */
        .state-wrapper {
          text-align: center;
          padding: 80px 20px;
          color: var(--text-muted);
          background: var(--card-bg);
          border: 1px dashed var(--card-border);
          border-radius: 24px;
        }
        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        /* RESPONSIVE */
        @media (max-width: 768px) {
          .event-wrapper { padding: 1.5rem; border-radius: 24px; }
          .event-header { flex-direction: column; align-items: stretch; gap: 1rem; }
          .search-bar { width: 100%; }
          .form-row { grid-template-columns: 1fr; gap: 0; }
          .modal-content { padding: 1.5rem; }
          .event-grid { grid-template-columns: 1fr; }
          .actions-top { flex-direction: column; }
        }
          @media (min-width: 1024px) {
  .event-grid {
    grid-template-columns: repeat(3, 1fr);
  }
      `}</style>

      {/* HEADER */}
      <div className="event-header">
        <h2>
          <div className="icon-wrapper">
            <FontAwesomeIcon icon={faCalendarAlt} />
          </div>
          Événements
          <span className="event-count">{events.length}</span>
        </h2>
        <div className="actions-top">
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            onClear={() => setSearchTerm('')} 
          />
          <button className="btn-primary" onClick={openAddModal}>
            <FontAwesomeIcon icon={faPlus} /> Nouvel Événement
          </button>
        </div>
      </div>

      {/* CONTENU */}
      {loading ? (
        renderSkeletons()
      ) : error ? (
        <div className="state-wrapper" style={{ borderColor: 'rgba(239,68,68,0.3)', color: '#ef4444' }}>
          <FontAwesomeIcon icon={faExclamationCircle} size="3x" />
          <p>{error}</p>
        </div>
      ) : events.length === 0 ? (
        <div className="state-wrapper">
          <div className="empty-icon">📂</div>
          <p>Aucun événement ne correspond à votre recherche.</p>
        </div>
      ) : (
        <div className="event-grid">
          {events.map(ev => (
            <EventCard 
              key={ev.id} 
              event={ev} 
              onEdit={openEditModal} 
              onDelete={handleDelete} 
            />
          ))}
        </div>
      )}

      {/* MODAL CRÉATION / ÉDITION */}
      <div className={`modal-overlay ${showModal ? 'active' : ''}`} onClick={closeModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={closeModal} aria-label="Fermer">
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <h3>
            <FontAwesomeIcon icon={editingId ? faEdit : faPlus} />
            {editingId ? 'Modifier l\'événement' : 'Créer un événement'}
          </h3>
          <form onSubmit={handleSubmit} noValidate>
            {formError && (
              <div className="error-message">
                <FontAwesomeIcon icon={faTimes} /> {formError}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="titres">Titre *</label>
              <input
                type="text"
                id="titres"
                name="titres"
                value={formData.titres}
                onChange={handleInputChange}
                required
                placeholder="Ex: Conférence Tech 2026"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                placeholder="Décrivez les points clés de l'événement..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="adresse">Lieu *</label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleInputChange}
                required
                placeholder="Ex: Palais des Congrès, Paris"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dates">Date *</label>
                <input
                  type="date"
                  id="dates"
                  name="dates"
                  value={formData.dates}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="heures">Heure *</label>
                <input
                  type="time"
                  id="heures"
                  name="heures"
                  value={formData.heures}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="images">Affiche / Image (Optionnel)</label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="images"
                  name="images"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </div>
              {(formImagePreview || existingImagePath) && (
                <div className="preview-wrapper">
                  <img 
                    src={formImagePreview || `${API_CONFIG.apiUrl}${existingImagePath}`} 
                    alt="Aperçu" 
                    className="preview-image" 
                  />
                  <button 
                    type="button" 
                    className="remove-image-btn" 
                    onClick={removeImage}
                    aria-label="Supprimer l'image"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={closeModal}>Annuler</button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Enregistrement...</>
                ) : (
                  <><FontAwesomeIcon icon={faChevronRight} /> {editingId ? 'Mettre à jour' : 'Créer'}</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* CONFIRMATION SUPPRESSION */}
      <DeleteConfirmModal
        isOpen={deleteConfirm.isOpen}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, eventId: null, eventTitle: '' })}
        eventTitle={deleteConfirm.eventTitle}
      />

      {/* TOAST */}
      {toast.visible && (
        <div className="toast-container">
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast({ ...toast, visible: false })} 
          />
        </div>
      )}
    </div>
  );
};

export default EvenementList;